import { CommonModule } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
  signal,
} from '@angular/core';
import type { WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { PdfJsViewerModule } from 'ng2-pdfjs-viewer';
import { Labels } from '../../../constants/labels-constant';
import { ValidationMessagesTexts } from '../../../constants/validation-messages.constants';
import {
  ChangeCandidate,
  ChangeProvenance,
  ReviewSection,
  isVerifyFlag,
} from '../../../models/v2models/review-state';
import { ReportPdfType, ReviewStateService } from '../../../services/review-state.service';
import { ReportV2Service } from '../../../services/report-v2.service';
import { SnackbarService } from '../../../services/snackbar.service';

/**
 * Screen 04 · Section detail — preview report vs new report.
 *
 * Opened once per section. The upper pane is the current version, the lower pane the same
 * content with every proposed edit applied; each proposed edit is then listed below with its
 * own Decline / Edit / Accept. A section with nothing proposed opens straight into the
 * whole-section editor (US-14-01).
 */
@Component({
  selector: 'app-change-detail-popup',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, PdfJsViewerModule],
  templateUrl: './change-detail-popup.html',
  styleUrl: './change-detail-popup.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ChangeDetailPopup implements OnInit, OnChanges {
  private reviewStateService = inject(ReviewStateService);
  private reportV2Service = inject(ReportV2Service);
  private snackbar = inject(SnackbarService);

  @Input({ required: true }) reportId!: number;
  @Input({ required: true }) section!: ReviewSection;
  /** The section body as plain text, for the whole-section editor. */
  @Input() sectionText = '';
  /** Opens straight into the section editor, used for sections with nothing proposed. */
  @Input() startInSectionEdit = false;
  /** Change ids currently being accepted, supplied by the parent while API calls are in flight. */
  @Input() acceptingChangeIds: readonly number[] = [];

  @Output() confirmChange = new EventEmitter<ChangeCandidate>();
  @Output() declineChange = new EventEmitter<ChangeCandidate>();
  @Output() saveEdit = new EventEmitter<{ change: ChangeCandidate; text: string }>();
  @Output() saveSection = new EventEmitter<string>();
  @Output() sectionSaved = new EventEmitter<number>();
  @Output() closed = new EventEmitter<void>();

  labels = Labels;

  readonly showProvenance = true;

  readonly currentPdf = signal<Blob | null>(null);
  readonly updatedPdf = signal<Blob | null>(null);
  /** A pane that failed to load says so rather than showing stand-in content. */
  readonly currentFailed = signal(false);
  readonly updatedFailed = signal(false);
  readonly isCurrentLoading = signal(false);
  readonly isUpdatedLoading = signal(false);

  /** The proposed edit whose textarea is open, if any. */
  readonly editingChangeId = signal<number | null>(null);
  editDraft = '';

  /** Whole-section editing, for a section with nothing proposed. */
  readonly isSectionEditing = signal(false);
  sectionDraft = '';

  /** A regeneration is in flight, so the pane's refresh shows as busy. */
  readonly isRefreshing = signal(false);

  /** Loading state when fetching section edit text for unchanged sections. */
  readonly isSectionEditLoading = signal(false);

  ngOnInit(): void {
    if (this.startInSectionEdit) {
      this.startSectionEdit();
    }

    this.openPanes();
  }

  /** Any change that has been accepted, edited or declined — i.e. no longer untouched. */
  hasActedChanges(): boolean {
    return this.section.changes.some((change) => change.status !== 'Pending');
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Keep Accept/Decline/Edit decisions side-effect free: parent state updates must not trigger
    // preview/current-pane API calls. Pane refresh is explicit via the Refresh action.
    if (changes['section'] && !changes['section'].firstChange) {
      return;
    }
  }

  /**
   * Initial open behavior:
   * - Left pane: fetch prior report PDF once (type 1).
   * - Right pane: generate preview first, then fetch preview PDF (type 2).
   */
  private openPanes(): void {
    this.loadCurrentPane();
    this.loadPreviewPane();
  }

  loadCurrentPane(): void {
    this.isCurrentLoading.set(true);
    this.reportV2Service.getReportPdfStream(this.reportId, ReportPdfType.PriorReport).subscribe({
      next: (response) => this.setPane(response.body, this.currentPdf, this.currentFailed),
      error: () => {
        this.currentFailed.set(true);
        this.isCurrentLoading.set(false);
      },
      complete: () => this.isCurrentLoading.set(false),
    });
  }

  loadPreviewPane(): void {
    this.updatedFailed.set(false);
    this.isUpdatedLoading.set(true);
    this.reviewStateService.generateQuickPreview(this.reportId).subscribe({
      next: () => {
        this.reportV2Service.getReportPdfStream(this.reportId, ReportPdfType.Preview).subscribe({
          next: (response) => this.setPane(response.body, this.updatedPdf, this.updatedFailed),
          error: () => {
            this.updatedFailed.set(true);
            this.isUpdatedLoading.set(false);
          },
          complete: () => this.isUpdatedLoading.set(false),
        });
      },
      error: () => {
        this.updatedFailed.set(true);
        this.isUpdatedLoading.set(false);
      },
    });
  }

  /**
   * Regenerate the preview: queue it, then stream the freshly rendered PDF into the pane. The
   * fetch only runs once the generation call has succeeded.
   */
  refreshPreview(): void {
    this.isRefreshing.set(true);
    this.updatedFailed.set(false);
    this.isUpdatedLoading.set(true);
    this.reviewStateService.generateQuickPreview(this.reportId).subscribe({
      next: () => {
        this.reportV2Service.getReportPdfStream(this.reportId, ReportPdfType.Preview).subscribe({
          next: (response) => this.setPane(response.body, this.updatedPdf, this.updatedFailed),
          error: () => {
            this.updatedFailed.set(true);
            this.isUpdatedLoading.set(false);
            this.isRefreshing.set(false);
          },
          complete: () => {
            this.isUpdatedLoading.set(false);
            this.isRefreshing.set(false);
          },
        });
      },
      error: () => {
        this.updatedFailed.set(true);
        this.isUpdatedLoading.set(false);
        this.isRefreshing.set(false);
      },
    });
  }

  /** No document means the pane reports the failure — never stand-in content. */
  private setPane(
    blob: Blob | null,
    pdf: WritableSignal<Blob | null>,
    failed: WritableSignal<boolean>,
  ): void {
    pdf.set(blob);
    failed.set(blob === null);
  }

  // ---------------------------------------------------------------- per-change state

  effectiveValue(change: ChangeCandidate): string {
    // Edited values must win over the original proposal, otherwise a saved edit keeps showing
    // the proposed text in the green diff.
    return change.editedText ?? change.editedHtml ?? change.proposedText ?? change.proposedHtml ?? '';
  }

  needsValue(change: ChangeCandidate): boolean {
    return this.effectiveValue(change).trim().length === 0;
  }

  /**
   * A deliberate edit is its own decision: the edited candidate is stored and applied to the
   * preview and final document (the server folds Edited into Accepted), so it no longer needs
   * an Accept/Decline.
   */
  isDecided(change: ChangeCandidate): boolean {
    return (
      change.status === 'Accepted' || change.status === 'Declined' || change.status === 'Edited'
    );
  }

  isEditing(change: ChangeCandidate): boolean {
    return this.editingChangeId() === change.id;
  }

  isAccepting(change: ChangeCandidate): boolean {
    return this.acceptingChangeIds.includes(change.id);
  }

  /** Verify flags are prompts to read the section, not edits, so they are not listed here. */
  proposedChanges(): ChangeCandidate[] {
    return this.section.changes.filter((change) => !isVerifyFlag(change));
  }

  isHeuristic(change: ChangeCandidate): boolean {
    return change.matchKind === 'Heuristic';
  }

  isEdited(change: ChangeCandidate): boolean {
    return change.status === 'Edited';
  }

  /** The phrase this edit replaces, named in the editor so the target is unambiguous. */
  originalPhrase(change: ChangeCandidate): string {
    return change.originalText ?? this.toPlainText(change.originalHtml ?? '');
  }

  sourceRef(provenance: {
    fileName: string;
    sheetName?: string | null;
    sheet?: string | null;
    cell?: string | null;
    cellRef?: string | null;
    columnName?: string | null;
    value?: string | null;
    rawValue?: string | null;
  }, sourceType?: string): string {
    const source = (sourceType ?? '').trim();
    const normalizedSource = this.normalizeSourceType(source, provenance);

    if (normalizedSource === 'Input Variables') {
      return [normalizedSource, provenance.columnName, provenance.value ?? provenance.rawValue]
        .filter((part) => (part ?? '').toString().trim().length > 0)
        .join(' - ');
    }

    if (normalizedSource === 'Data File') {
      return [
        normalizedSource,
        provenance.fileName,
        provenance.sheetName ?? provenance.sheet,
        provenance.value ?? provenance.rawValue,
      ]
        .filter((part) => (part ?? '').toString().trim().length > 0)
        .join(' - ');
    }

    const sheet = provenance.sheetName ?? provenance.sheet;
    const cell = provenance.cell ?? provenance.cellRef ?? provenance.columnName;
    const location = [sheet, cell].filter((part) => (part ?? '').trim().length > 0).join('!');
    return location ? `${provenance.fileName} · ${location}` : provenance.fileName;
  }

  private normalizeSourceType(
    sourceType: string,
    provenance: {
      fileName: string;
      columnName?: string | null;
      sheetName?: string | null;
      sheet?: string | null;
    },
  ): 'Input Variables' | 'Data File' | '' {
    const source = sourceType.trim().toLowerCase();
    if (/^input[\s_-]*variables?$/.test(source)) {
      return 'Input Variables';
    }
    if (/^data[\s_-]*files?$/.test(source)) {
      return 'Data File';
    }

    // Fallback inference when source type is absent/inconsistent from API.
    const fileName = (provenance.fileName ?? '').trim().toLowerCase();
    const columnName = (provenance.columnName ?? '').trim().toLowerCase();
    if (fileName === 'metadata' || columnName.startsWith('metadata.')) {
      return 'Input Variables';
    }
    if (fileName.length > 0 || !!(provenance.sheetName ?? provenance.sheet)) {
      return 'Data File';
    }
    return '';
  }

  provenanceList(change: ChangeCandidate): ChangeProvenance[] {
    return change.provenances ?? change.provenance ?? [];
  }

  hasProvenance(change: ChangeCandidate): boolean {
    return this.provenanceList(change).length > 0;
  }

  sourceLine(change: ChangeCandidate): string {
    const provenances = this.provenanceList(change);
    if (provenances.length > 0) {
      return provenances
        .map((item) => this.sourceRef(item, change.source))
        .filter((line) => line.trim().length > 0)
        .join('; ');
    }
    return '';
  }

  rationaleLine(change: ChangeCandidate): string {
    return (change.rationale ?? '').trim();
  }

  // ---------------------------------------------------------------- editing a proposed edit

  startEdit(change: ChangeCandidate): void {
    this.editDraft = this.toPlainText(this.effectiveValue(change));
    this.editingChangeId.set(change.id);
  }

  cancelEdit(): void {
    this.editingChangeId.set(null);
    this.editDraft = '';
  }

  /** Saving stores the value and leaves the change pending — accepting is a separate step. */
  saveDraft(change: ChangeCandidate): void {
    const text = this.editDraft.trim();
    if (!text) return;
    this.saveEdit.emit({ change, text });
    this.cancelEdit();
  }

  onConfirm(change: ChangeCandidate): void {
    if (this.isAccepting(change)) return;
    this.confirmChange.emit(change);
  }

  onDecline(change: ChangeCandidate): void {
    this.declineChange.emit(change);
  }

  // ---------------------------------------------------------------- editing the whole section

  startSectionEdit(): void {
    // For unchanged sections, fetch the edit text and version from the API
    if (!this.section.changed) {
      this.isSectionEditLoading.set(true);
      this.reportV2Service.getSectionEditText(this.reportId, this.section.id).subscribe({
        next: (response) => {
          this.isSectionEditLoading.set(false);
          if (!response.success || !response.data) {
            this.snackbar.showCustom({
              type: 'error',
              title: ValidationMessagesTexts.TOASTER.ERROR.TITLE,
              message: response.message || Labels.REVIEW_REFINE.TOASTER.SECTION_LOAD_FAILED,
            });
            return;
          }

          // Store version in the section for later use in save
          this.section.version = response.data.version;
          // Bind the text from the API response
          this.sectionDraft = response.data.text || this.sectionText;
          this.isSectionEditing.set(true);
        },
        error: () => {
          this.isSectionEditLoading.set(false);
          this.snackbar.showCustom({
            type: 'error',
            title: ValidationMessagesTexts.TOASTER.ERROR.TITLE,
            message: Labels.REVIEW_REFINE.TOASTER.SECTION_LOAD_FAILED,
          });
        },
      });
    } else {
      // For changed sections, just use the provided section text
      this.sectionDraft = this.sectionText;
      this.isSectionEditing.set(true);
    }
  }

  /**
   * The section editor is the whole reason this popup opened, so cancelling it closes the
   * popup rather than dropping the reviewer onto an empty dialog.
   */
  cancelSectionEdit(): void {
    this.sectionDraft = '';
    this.isSectionEditing.set(false);
    this.close();
  }

  saveSectionDraft(): void {
    if (!this.sectionDraft.trim()) return;
    
    // For unchanged sections, save via API with version; for changed sections, emit to parent
    if (!this.section.changed) {
      this.reportV2Service.updateSectionContent(this.reportId, this.section.id, this.sectionDraft, this.section.version).subscribe({
        next: (response) => {
          if (!response.success) {
            this.snackbar.showCustom({
              type: 'error',
              title: ValidationMessagesTexts.TOASTER.ERROR.TITLE,
              message: response.message || Labels.REVIEW_REFINE.TOASTER.SECTION_SAVE_FAILED,
            });
            return;
          }

          this.snackbar.showCustom({
            type: 'success',
            title: ValidationMessagesTexts.TOASTER.SUCCESS.TITLE,
            message: Labels.REVIEW_REFINE.TOASTER.SECTION_SAVED,
          });
          this.refreshPreview();
          this.isSectionEditing.set(false);
          this.sectionSaved.emit(this.section.id);
        },
        error: (err) => {
          // 409 means the document moved on since this section was opened. Stay in edit
          // mode so the typed text survives, and ask the writer to reload.
          const isStale = err?.status === 409;
          this.snackbar.showCustom({
            type: 'error',
            title: ValidationMessagesTexts.TOASTER.ERROR.TITLE,
            message: isStale
              ? err?.error?.message || Labels.REVIEW_REFINE.TOASTER.SECTION_RELOAD_REQUIRED
              : err?.error?.message || Labels.REVIEW_REFINE.TOASTER.SECTION_SAVE_FAILED,
          });
        },
      });
    } else {
      // For changed sections, use the parent's save flow
      this.saveSection.emit(this.sectionDraft);
      this.isSectionEditing.set(false);
    }
  }

  close(): void {
    this.closed.emit();
  }

  private toPlainText(html: string): string {
    const container = document.createElement('div');
    container.innerHTML = html;
    return (container.textContent || '').trim();
  }
}
